import { CasClient } from '@central-products/cas-client';
import { RETRYABLE_DELAY_INITIAL_TIME } from './constants';
import { retryable, abortable, RetryableBreakError, RetryableAbortedError, SessionExistsError } from '../libs';
import { PassiveLoginStrategyInterface } from '../types';

export class PassiveLoginPollingStrategy implements PassiveLoginStrategyInterface {
  private abortController?: AbortController;

  constructor(
    private config: {
      casClient: CasClient;
      onLoginCallback: () => void;
    }
  ) {}

  public async init() {
    try {
      this.abortController = new AbortController();

      await Promise.race([this.watchAbort(), this.watchLogin()]);
    } catch (error) {
      this.close();

      if (error instanceof SessionExistsError || error instanceof RetryableBreakError) {
        this.config.onLoginCallback();
      }
    }
  }

  public close() {
    if (!this.abortController) {
      return;
    }

    if (!this.abortController.signal.aborted) {
      this.abortController.abort();
    }
  }

  private watchAbort() {
    return new Promise((_, reject) => {
      const onAbortCallback = () => {
        this.abortController!.signal.removeEventListener('abort', onAbortCallback);
        reject(new RetryableAbortedError());
      };

      this.abortController!.signal.addEventListener('abort', onAbortCallback);
    });
  }

  private watchLogin() {
    return retryable(
      async () => {
        /**
         * Method `close` was invoked and any further calls should be prevented.
         */
        if (this.abortController!.signal.aborted) {
          throw new RetryableAbortedError();
        }

        /**
         * Attempt to check if CAS session exists.
         *
         * If this call resolves this means valid CAS session is found or one is created
         * based on `cust_*` cookies (when `custLogin` flag is set to `true`) and we should
         * throw `SessionExistsError` in order to break the loop of polling CAS for this information
         * and inform CAS session manager to change it's state into `LOGGED_IN`.
         */
        await abortable(() => this.config.casClient.init({ custLogin: true }), {
          signal: this.abortController!.signal,
        });
        throw new SessionExistsError();
      },
      {
        delay: {
          initialTime: RETRYABLE_DELAY_INITIAL_TIME,
          maxTime: Infinity,
          exponentialBackoff: false,
          skipFirstDelay: false,
          jitter: false,
        },
        retry: {
          predicate: (error: Error) => {
            if (error instanceof SessionExistsError) {
              /**
               * In case of `SessionExistsError` error we need to break
               * and do not try to retry anymore as there is CAS session found.
               */
              return false;
            }

            if (error instanceof DOMException && error.name === 'AbortError') {
              return false;
            }

            return !(error instanceof RetryableBreakError) && !(error instanceof RetryableAbortedError);
          },
        },
      }
    );
  }
}
